"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTour } from "@/context/TourContext";
import { useEffect, useState } from "react";
import { FiArrowRight, FiSettings, FiUpload } from "react-icons/fi";

const DataImportIntroModal = () => {
  const { tourState, closeImportIntroModal } = useTour();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (tourState.showImportIntroModal) {
      // Small delay to ensure smooth entrance
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [tourState.showImportIntroModal]);

  const handleImportData = () => {
    closeImportIntroModal("import");
  };

  const handleSkipForNow = () => {
    closeImportIntroModal();
  };

  return (
    <Dialog
      open={tourState.showImportIntroModal}
      onOpenChange={() => {
        if (!tourState.showImportIntroModal) {
          handleSkipForNow();
        }
      }}
    >
      <DialogContent
        className="max-w-2xl"
        closeButton={false}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-2xl flex items-center gap-4 text-center justify-center transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            ¿Tienes datos para importar?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Main content */}
          <div
            className={`text-center transition-all duration-700 ease-out delay-200 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <p className="text-gray-600 mb-4">
              Si tienes información en otros sistemas, podemos ayudarte a
              importarla.
            </p>
          </div>

          {/* Options */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-700 ease-out delay-300 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            {/* Import option */}
            <Card className="border-2 border-ac-3 hover:border-ac transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 bg-ac-4 rounded-full flex items-center justify-center group-hover:bg-ac-3 transition-all duration-300 group-hover:scale-110">
                    <FiUpload className="h-8 w-8 text-ac transition-transform duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      Sí, importar
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Te guiamos paso a paso
                    </p>
                  </div>
                  <Button
                    onClick={handleImportData}
                    className="w-full flex items-center gap-2"
                  >
                    Importar ahora
                    <FiArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Skip option */}
            <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-all duration-300">
                    <FiArrowRight className="h-8 w-8 text-gray-600 transition-transform duration-300 " />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      No, empezar limpio
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Agregar datos directamente
                    </p>
                  </div>
                  <Button
                    onClick={handleSkipForNow}
                    variant="outline"
                    className="w-full flex items-center gap-2"
                  >
                    Continuar sin importar
                    <FiArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional info */}
          <div
            className={`bg-ac-4 border border-ac-3 rounded-lg p-3 transition-all duration-700 ease-out delay-500 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            <div className="flex items-center gap-2 text-sm text-ac+1">
              <FiSettings className="h-4 w-4 text-ac flex-shrink-0" />
              <span>
                Siempre podrás importar más adelante. La opción está en la
                sección de ajustes
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataImportIntroModal;
